import { DEFAULT_MAX_MB } from "./attachment";
import { apiFetch } from "../api/client";
import { shownIds } from "./conditions";

/**
 * THE EVENT'S FORMS — the product's own, not a builder of our own.
 *
 *   GET /domains/:d/events/:e/forms        the forms, each with its fields
 *   GET /domains/:d/forms/data?eventId&userId    what this participant answered
 *   PUT /domains/:d/forms/data?eventId&userId    save
 *
 * ⚠️ THE FORM IS NOT BUILT HERE, IT IS CHOSEN. The prototype invents a field builder
 * because it has no database behind it; the product keeps its forms in the back
 * office and a setting names the one this step serves. Reproducing the builder would
 * have meant a second, divergent definition of every field.
 *
 * ⚠️ A FIELD'S VALUE LIVES UNDER ITS MODEL, and possibly inside `extended`. The
 * answers come back as `{ U: {...}, UE: {...}, C: {...} }` — one record per model,
 * each with its own `extended` bag. So `firstName` on `U` is `data.U.firstName`,
 * while a custom question is `data.U.extended[key]`. Read the wrong one and the form
 * opens empty over answers that are there.
 */

/** What the API calls a "model" — which record a field belongs to. */
export type Model = "U" | "UE" | "C";

/**
 * ⚠️ `id` ET `value` NE SONT PAS LA MÊME CHOSE, et les confondre coûte cher. `value`
 * est la CLÉ de l'option — ce qui s'enregistre dans la réponse du participant. `id` est
 * l'identifiant de sa ligne `Translations` — ce sur quoi la V1 et le back office
 * bâtissent la clé de TRADUCTION (`${form}-${model}-${id}`). Écrire une traduction sous
 * la clé d'option la rendrait invisible aux deux autres fronts, et réciproquement.
 */
export type Option = {
  id: number | null;
  label: string;
  value: string;
  disabled: boolean;
};

export type Field = {
  /** Stable within the form — used as the React key and the input id. */
  id: string;
  /** Where the value is stored. */
  key: string;
  model: Model;
  extended: boolean;
  /**
   * L'ÉTIQUETTE VUE PAR LE PARTICIPANT — celle du formulaire, sinon celle de la
   * question.
   *
   * ⚠️ DEUX LIBELLÉS EXISTENT, ET LA V2 N'EN LISAIT QU'UN. `Translations.value` est
   * celui de la QUESTION, partagé par tous les formulaires du domaine ;
   * `FormModels.label` est celui de CE formulaire, et il passe devant. La règle est
   * celle de la V1 : `model.label || model.value`.
   *
   * Ne lire que `value` n'était pas un détail cosmétique : sur l'événement de test,
   * 27 poses sur 36 n'ont pas de libellé propre — et quatre des cinq champs du
   * formulaire 5525 ont une QUESTION sans libellé, donc s'affichaient au participant
   * avec une étiquette VIDE alors que le formulaire en portait une.
   */
  label: string;
  /**
   * Le libellé propre à cette pose, `""` s'il n'y en a pas — ce que l'éditeur modifie.
   * Distinct de `label`, qui est déjà le résultat du repli : sans lui, impossible de
   * distinguer « aucun libellé propre » de « un libellé propre identique à celui de la
   * question », et l'éditeur aurait écrit l'un en croyant lire l'autre.
   */
  ownLabel: string;
  /**
   * Le libellé de la QUESTION partagée, `""` si elle n'en a pas — ce sur quoi le champ
   * retombe quand la pose n'a pas le sien. L'éditeur le montre en filigrane, pour
   * qu'on voie ce qu'on obtiendrait en effaçant. Irrécupérable depuis `label` dès
   * qu'une exception existe : les deux se confondraient.
   */
  labelQuestion: string;
  /**
   * ⚠️ LES DIX QUE LE BACK OFFICE PROPOSE, DANS SES TROIS GROUPES — la liste se relit
   * telle quelle dans `bo/src/pages/forms/v2/settings.js` (`components`,
   * `componentsMultiple`, `decoratorComponents`).
   *
   * ⚠️ EN OUBLIER UN NE LE DÉGRADE PAS : IL DISPARAÎT. Tout ce qui n'est pas dans
   * `COMPONENTS` est écarté par `fieldOf`, silencieusement. `input-number` et
   * `textarea` manquaient : une question « Nombre » ou « Texte sur plusieurs lignes »
   * posée au back office n'existait pas dans le parcours — pas vide, pas grisée,
   * ABSENTE — et l'organisateur n'avait aucun moyen de s'en apercevoir depuis son
   * formulaire, où elle s'affichait très bien.
   */
  component:
    | "input"
    | "input-number"
    | "textarea"
    | "phone-number"
    | "select"
    | "select-multiple"
    | "radio"
    | "checkbox"
    | "comment"
    | "html"
    | "file";
  required: boolean;
  /**
   * ⚠️ « NON MODIFIABLE » NE VEUT PAS DIRE « GRISÉ D'EMBLÉE ». C'est la règle de la
   * V1, et elle est essentielle : `disabled = valueOld && model.isDisabled` — le
   * champ se verrouille une fois qu'il PORTE une valeur, pas avant. Traduit en
   * « toujours désactivé », un champ obligatoire marqué non modifiable devenait
   * impossible à remplir, donc une inscription impossible à terminer.
   *
   * L'écran décide donc, en regardant ce que le serveur détient déjà : voir
   * `Informations`.
   */
  locked: boolean;
  options: Option[];
  /** For `comment` and `html`: the text or markup to display, not a value to fill. */
  content: string;
  /**
   * LA LARGEUR OPTIMALE — ce que le champ demande, pas ce qu'il obtient.
   *
   * L'écran la traduit en classe, et la feuille fait le reste : tant que ça tient, les
   * champs se rangent côte à côte ; sinon celui-ci passe à la ligne et l'occupe en
   * entier. Voir `.form` / `--f-opt` dans `chassis.css`.
   *
   * ⚠️ ABSENTE, ELLE VAUT « PLEINE », et c'est délibéré : c'est la mise en page
   * d'avant, où tout portait `f--wide`. Aucun formulaire déjà configuré ne change
   * d'allure parce qu'on a déployé ceci — la répartition n'apparaît que là où
   * quelqu'un l'a réglée.
   */
  width: Width;
  /**
   * ⚠️ L'IDENTIFIANT DE LA QUESTION, À NE PAS CONFONDRE AVEC `id`. `id` est celui du
   * PLACEMENT (`FormModels.id`) — ce champ-ci, dans ce formulaire-ci. `modelId` est
   * celui de la QUESTION (`Translations.id`), partagée par tous les formulaires du
   * domaine. L'éditeur a besoin des deux : il modifie des placements, et le serveur
   * les réinsère en les rattachant à leur question.
   */
  modelId: number | null;
  /**
   * NE S'AFFICHE QUE SI un autre champ a reçu l'une de ces réponses. `modelId` désigne
   * la QUESTION parente (pas le placement), `keys` les clés de ses options. `null` :
   * toujours affiché. La règle est dans `funnel/conditions.ts`.
   */
  condition: { modelId: number; keys: string[] } | null;
  /**
   * LES CHOIX L'UN SOUS L'AUTRE, plutôt que côte à côte.
   *
   * ⚠️ LE RÉGLAGE EXISTE DEPUIS TOUJOURS ET LA V2 L'IGNORAIT. `metadata.valuesColumn`
   * est lu par la V1 ET par le back office, tous deux de la même façon : présent, les
   * options s'empilent ; absent, elles se suivent côte à côte. La V2 les empilait
   * TOUJOURS — un « Oui / Non » occupait deux lignes pleines, et l'organisateur qui
   * avait réglé le contraire ne voyait aucune différence.
   *
   * Comme pour `metadata.comment` et le libellé du formulaire, ce n'est donc pas un
   * changement de goût : c'est un réglage configuré qu'on cessait de lire.
   */
  choicesInColumn: boolean;
  /**
   * POUR « PIÈCE JOINTE » : ce que le back office accepte (syntaxe de l'attribut
   * `accept`, vide = tout) et le plafond en mégaoctets. Voir `funnel/attachment.ts`.
   */
  accept: string;
  maxMb: number;
};

/** Les quatre tailles que l'organisateur peut choisir. */
export const WIDTHS = ["petit", "moyen", "grand", "pleine"] as const;
export type Width = (typeof WIDTHS)[number];

/**
 * ⚠️ `type` EST CELUI DE L'API, et il ne se déduit pas des champs : `'U'` pour un
 * formulaire de participant, `'C'` pour un formulaire de société, `null` pour des
 * lignes héritées (un champ, sans modèle). L'événement de test en porte 15 dont
 * seulement 9 sont des formulaires de participant — offrir les autres au choix de
 * l'étape « informations », c'est offrir un formulaire de société à un visiteur, ou
 * une ligne vide dont le seul champ est écarté faute de modèle.
 */
export type Form = {
  id: number;
  name: string;
  type: "U" | "C" | null;
  /**
   * ═══ CELUI DE L'ÉVÉNEMENT, OU CELUI DU DOMAINE ═══
   *
   * ⚠️ LA ROUTE REND LES DEUX, ET C'EST VOULU : un domaine porte des formulaires
   * réutilisables d'un événement à l'autre (`F.eventId IS NULL`), et chaque événement
   * peut avoir les siens. Mélangés sans distinction dans un sélecteur, deux
   * « Inscription » se ressemblent à s'y méprendre, et on ne sait pas lequel on choisit
   * ni ce qu'on casse en le modifiant — modifier celui du domaine touche TOUS les
   * événements qui le servent.
   */
  eventId: number | null;
  fields: Field[];
  /**
   * ⚠️ LES LIGNES TELLES QUE L'API LES A RENDUES, gardées à côté des `fields`. Ce n'est
   * pas de la redondance : `PUT /forms/{id}` SUPPRIME toutes les lignes du formulaire
   * puis réinsère celles qu'on lui envoie. Réécrire depuis `fields` — d'où `fieldOf` a
   * retiré `format`, `metadata.css`, l'affichage conditionnel, et toute ligne dont il
   * ne connaît pas le composant — effacerait ces réglages du back office à la première
   * sauvegarde. L'éditeur modifie donc CECI, par petites touches, et le renvoie entier.
   */
  models: RawModel[];
};

/** Components that display something instead of collecting it. */
export const DECORATIVE = new Set(["comment", "html"]);
/** Components whose value is a LIST. */
export const MULTIPLE = new Set(["select-multiple", "checkbox"]);

const COMPONENTS = new Set<Field["component"]>([
  "input",
  "input-number",
  "textarea",
  "phone-number",
  "select",
  "select-multiple",
  "radio",
  "checkbox",
  "comment",
  "html",
  "file",
]);

type RawOption = {
  id?: number | null;
  label?: string | null;
  value?: string | null;
  disabled?: unknown;
};

/**
 * Une ligne de formulaire telle que l'API l'envoie, avant `fieldOf`.
 *
 * ⚠️ ELLE PORTE PLUS QUE CE QUE LA V2 SAIT LIRE, et c'est pour ça qu'on la garde
 * entière : `format` (majuscules automatiques), `metadata.css`, `conditionModelId`,
 * `conditionKeys`, `valuesColumn`, `keysToHide`, `uid` — des fonctions réglées au back
 * office dont cet écran ne fait rien. Réécrire un formulaire depuis les seuls champs
 * que `fieldOf` retient les effacerait toutes, parce que `PUT /forms/{id}` supprime
 * puis réinsère. D'où `[RAW]` sur `Field` : voir `funnel/formEditor.ts`.
 */
export type RawModel = {
  id?: number;
  /** `Translations.id` — la question, partagée entre formulaires. */
  modelId?: number | null;
  key?: string | null;
  value?: string | null;
  model?: string | null;
  component?: string | null;
  isRequired?: unknown;
  isDisabled?: unknown;
  isExtended?: unknown;
  order?: number | null;
  metadata?: Record<string, unknown> | null;
  values?: RawOption[] | null;
  /**
   * Colonnes de `FormModels` que cet écran n'utilise pas mais qui doivent survivre à
   * une réécriture : le libellé propre à CE formulaire (distinct de `value`, celui de
   * la question partagée), et la mise en forme automatique (`capitalize-words`,
   * `uppercase`) que la V1 et le back office appliquent.
   */
  label?: string | null;
  format?: string | null;
  /** Tout le reste, que la V2 ne nomme pas et se contente de faire suivre. */
  [other: string]: unknown;
};

const yes = (v: unknown) => v === 1 || v === true;

/**
 * LA LARGEUR DEMANDÉE, ou « pleine » à défaut.
 *
 * ⚠️ UNE VALEUR INCONNUE VAUT « PLEINE », ELLE NE PLANTE PAS ET NE S'INVENTE PAS. Le
 * back office écrit dans `metadata` ce qu'il veut ; le jour où quelqu'un y range
 * `width: "enorme"`, ou une largeur en pixels, le formulaire doit continuer à
 * s'afficher — au pire comme avant cette fonctionnalité.
 */
function widthOf(metadata: RawModel["metadata"]): Width {
  const raw = metadata?.width;
  return WIDTHS.includes(raw as Width) ? (raw as Width) : "pleine";
}

/**
 * LE TEXTE D'UN DÉCOR — et CHACUN A SA CLÉ.
 *
 * ⚠️ CE N'EST PAS `metadata.text`, ET ÇA NE L'A JAMAIS ÉTÉ. Le back office écrit le
 * commentaire dans `metadata.comment` et le markup dans `metadata.html` — c'est ce
 * que la V1 et le back office relisent tous les deux. En cherchant un `text` qui
 * n'existe nulle part, le commentaire ressortait vide, et une chaîne vide est
 * exactement ce que `Row` ne rend pas : le mot que l'organisateur avait écrit au
 * milieu de son formulaire ne s'affichait tout simplement pas.
 *
 * Le repli sur `html` masquait la moitié du bug : le composant `html`, lui, marchait
 * — par accident, en tombant sur la bonne clé au deuxième essai.
 */
function contentOf(
  component: Field["component"],
  metadata: RawModel["metadata"],
): string {
  const raw =
    component === "comment"
      ? metadata?.comment
      : component === "html"
        ? metadata?.html
        : undefined;
  return typeof raw === "string" ? raw : "";
}

/**
 * ⚠️ A ROW WITH NO COMPONENT IS DROPPED, and there are five of them on this event
 * alone: `component: null`, `key: null`, `model: null`. Rendered, they would be
 * empty fields nobody can fill and that save nothing.
 *
 * Exported for its test: this is where a question configured in the back office either
 * survives the trip or vanishes, and vanishing is silent.
 */
/**
 * La condition d'affichage telle que le back office l'écrit : les deux clés ensemble,
 * ou rien. Un parent sans clé (« afficher si… » laissé vide) n'est pas une condition :
 * la V1 l'ignore aussi (`conditionKeys?.length`).
 */
function conditionOf(
  metadata: RawModel["metadata"],
): Field["condition"] {
  const modelId = metadata?.conditionModelId;
  const keys = metadata?.conditionKeys;
  if (typeof modelId !== "number" || !Array.isArray(keys)) return null;
  const clean = keys.filter((k): k is string => typeof k === "string");
  return clean.length ? { modelId, keys: clean } : null;
}

export function fieldOf(raw: RawModel, index: number): Field | null {
  const component = raw.component as Field["component"] | null | undefined;
  if (!component || !COMPONENTS.has(component)) return null;

  const decorative = DECORATIVE.has(component);
  const model = (raw.model ?? "U") as Model;
  if (!decorative && (!raw.key || !["U", "UE", "C"].includes(model)))
    return null;

  return {
    id: String(raw.id ?? `${component}-${index}`),
    key: raw.key ?? "",
    model,
    extended: yes(raw.isExtended),
    // `||` et non `??`, comme la V1 : un libellé vide retombe sur celui de la question,
    // il ne fait pas taire le champ.
    label: raw.label?.trim() || raw.value?.trim() || "",
    ownLabel: raw.label?.trim() ?? "",
    labelQuestion: raw.value?.trim() ?? "",
    component,
    required: yes(raw.isRequired),
    locked: yes(raw.isDisabled),
    options: (raw.values ?? []).flatMap((o) =>
      o?.value == null
        ? []
        : [
            {
              id: typeof o.id === "number" ? o.id : null,
              label: o.label ?? String(o.value),
              value: String(o.value),
              disabled: yes(o.disabled),
            },
          ],
    ),
    // The back office stores a decorator's text in its metadata; there is no separate
    // column for it. See `contentOf` — each component has its own key.
    content: contentOf(component, raw.metadata),
    width: widthOf(raw.metadata),
    modelId: typeof raw.modelId === "number" ? raw.modelId : null,
    condition: conditionOf(raw.metadata),
    choicesInColumn: yes(raw.metadata?.valuesColumn),
    accept: typeof raw.metadata?.accept === "string" ? raw.metadata.accept : "",
    maxMb:
      typeof raw.metadata?.maxMb === "number" && raw.metadata.maxMb > 0
        ? raw.metadata.maxMb
        : DEFAULT_MAX_MB,
  };
}

export async function fetchForms(
  domainId: number,
  eventId: number,
  signal?: AbortSignal,
): Promise<Form[]> {
  const answer = await apiFetch<{
    forms?: Array<{
      id?: number;
      name?: string | null;
      type?: string | null;
      /** `null` pour un formulaire du domaine — voir `Form.eventId`. */
      eventId?: number | null;
      models?: RawModel[] | null;
    }>;
  }>(`/domains/${domainId}/events/${eventId}/forms`, { api: "apix", signal });

  return (answer?.forms ?? []).flatMap((f) => {
    if (typeof f?.id !== "number") return [];

    // The back office's own order, not the array's: a form reordered there must come
    // out reordered here. Trié UNE fois, et `fields` comme `models` en héritent — sans
    // quoi l'éditeur afficherait ses lignes dans un ordre et l'écran dans un autre.
    /* ⚠️ UNE LIGNE SANS IDENTIFIANT N'EST PAS UN CHAMP. Un formulaire tout juste créé
       revient d'apix avec une ligne fantôme, tout à `null`, sortie du `leftJoin` sur
       `FormModels`. Gardée, elle repartait au premier enregistrement et le faisait
       échouer sur `component` NOT NULL (#288). apix ne la rend plus, mais un apix pas
       encore redéployé, si. */
    const models = (f.models ?? [])
      .filter((m) => typeof m?.id === "number")
      .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));

    return [
      {
        id: f.id,
        name: f.name ?? `Formulaire ${f.id}`,
        type: f.type === "U" || f.type === "C" ? f.type : null,
        /* ⚠️ `null` VEUT DIRE « DU DOMAINE », et une absence de champ aussi : une réponse
           qui ne le porte pas décrit un formulaire dont on ne sait pas s'il est propre à
           l'événement. On le range alors du côté du domaine, qui est le cas le plus
           large — annoncer « celui de cet événement » à tort ferait modifier sans crainte
           un formulaire partagé. */
        eventId: typeof f.eventId === "number" ? f.eventId : null,
        fields: models.flatMap((m, i) => {
          const field = fieldOf(m, i);
          return field ? [field] : [];
        }),
        models,
      },
    ];
  });
}

/**
 * UNE QUESTION DU DOMAINE — ce qu'un formulaire peut PLACER, pas ce qu'il contient.
 *
 * ⚠️ C'EST L'AUTRE MOITIÉ DU MODÈLE, et la distinction commande tout l'éditeur. La
 * question vit dans `Translations` : sa clé, son libellé, l'enregistrement où sa
 * réponse atterrit, ses options. Elle est PARTAGÉE par tous les formulaires du
 * domaine. Un `FormModels` ne fait que la poser dans un formulaire, avec un composant
 * et un rang.
 *
 * D'où : ajouter un champ = poser une question qui existe déjà ; retirer un champ =
 * retirer sa pose. Ni l'un ni l'autre ne crée ou ne détruit une question — ça se fait
 * au back office, et ça toucherait tous les autres formulaires.
 */
export type Question = {
  /** `Translations.id` — ce qui devient `FormModels.modelId`. */
  id: number;
  key: string;
  label: string;
  model: Model;
  extended: boolean;
  /**
   * Ses choix, s'il y en a. Ils appartiennent à la QUESTION, pas au formulaire : les
   * poser ici sert uniquement à ce qu'un champ qu'on vient d'ajouter montre ses
   * options tout de suite, sans attendre un aller-retour au serveur.
   */
  options: Option[];
};

type RawQuestionOption = {
  id?: number | null;
  key?: string | null;
  value?: string | null;
  isDisabled?: unknown;
};

type RawQuestion = {
  id?: number;
  key?: string | null;
  value?: string | null;
  labelShort?: string | null;
  model?: string | null;
  isExtended?: unknown;
  parentKey?: string | null;
  values?: RawQuestionOption[] | null;
};

/**
 * ⚠️ `labelShort` D'ABORD, comme le back office (`mo.labelShort || mo.value`). C'est
 * le nom court que l'organisateur a donné pour les listes ; `value` est le libellé
 * complet, qui peut être une phrase entière et rend une liste illisible.
 *
 * Les lignes à `parentKey` sont des OPTIONS, pas des questions — la route les replie
 * déjà dans `values`, mais s'en assurer ici coûte une comparaison et évite d'offrir
 * « Oui » et « Non » au choix comme s'ils étaient des champs.
 */
export async function fetchQuestions(
  domainId: number,
  eventId: number,
  signal?: AbortSignal,
): Promise<Question[]> {
  const answer = await apiFetch<{ models?: RawQuestion[] }>(
    `/domains/${domainId}/events/${eventId}/models`,
    { api: "apix", signal },
  );

  return (answer?.models ?? []).flatMap((m) => {
    const q = questionOf(m);
    return q ? [q] : [];
  });
}

/** Exportée pour son test : c'est ici que les deux conventions se croisent. */
export function questionOf(m: RawQuestion): Question | null {
  const model = m.model as Model;
  if (typeof m.id !== "number" || m.parentKey) return null;
  if (!m.key || !["U", "UE", "C"].includes(model)) return null;
  return {
    id: m.id,
    key: m.key,
    label: m.labelShort?.trim() || m.value?.trim() || m.key,
    model,
    extended: yes(m.isExtended),
    /**
     * ⚠️ LES DEUX ROUTES INVERSENT `key` ET `value` SUR LES OPTIONS, et il faut le
     * savoir pour ne pas recopier de travers :
     *
     *   `/models` (ici)  — la ligne `Translations` brute : `key` = l'identifiant,
     *                      `value` = le LIBELLÉ. « JQupsOn » / « a ».
     *   `/forms`         — la jointure, déjà renommée : `value` = l'identifiant
     *                      (aliasé depuis `T2.key`), `label` = le libellé.
     *
     * `fieldOf` consomme la seconde forme. On traduit donc ici, une fois, plutôt
     * que de laisser deux conventions se croiser plus loin — auquel cas les options
     * s'afficheraient sous leur identifiant (« JQupsOn » au lieu de « a »), et la
     * réponse enregistrée serait le libellé au lieu de la clé.
     */
    options: (m.values ?? []).flatMap((o) =>
      o?.key == null
        ? []
        : [
            {
              id: typeof o.id === "number" ? o.id : null,
              label: o.value ?? String(o.key),
              value: String(o.key),
              disabled: yes(o.isDisabled),
            },
          ],
    ),
  };
}

/**
 * CRÉER UNE DONNÉE ÉTENDUE — pour CET événement, et lui seul.
 *
 * ⚠️ LA PORTÉE EST FIGÉE, ET C'EST TOUT CE QUI REND CETTE ÉCRITURE ACCEPTABLE ICI. Le
 * back office offre trois portées : « permanente » (`U`, sans événement — la donnée
 * suit la personne d'un salon à l'autre), « multi-événements » (`UE`, sans événement —
 * la question est partagée, la réponse change), et « événementielle » (`UE` + cet
 * événement). Seule la dernière est proposée ici.
 *
 * La raison est la même que celle qui a fait exclure la création du plan : une question
 * de domaine est PARTAGÉE, donc la renommer ou la supprimer touche les formulaires des
 * voisins — ce qu'un organisateur d'un seul événement ne devrait pas pouvoir décider.
 * Une question événementielle, elle, n'est visible par personne d'autre : le danger
 * n'existe pas, et l'interdit ne protégeait rien.
 *
 * ⚠️ LA ROUTE NE REND PAS L'IDENTIFIANT CRÉÉ — son `return {}` est littéral. On relit
 * donc la liste et on y retrouve la question par SA CLÉ, que l'on a générée et que l'on
 * connaît. D'où aussi le contrôle d'unicité avant l'envoi : deux questions sous la même
 * clé se confondraient au retour, et écriraient leurs réponses l'une sur l'autre.
 */
export async function createQuestion(
  domainId: number,
  eventId: number,
  name: string,
  options: ReadonlyArray<{ label: string }>,
  known: ReadonlyArray<Question>,
  signal?: AbortSignal,
): Promise<Question> {
  const taken = new Set(known.map((q) => q.key));
  const key = freeKey(taken);

  await apiFetch(`/domains/${domainId}/events/${eventId}/models`, {
    api: "apix",
    method: "POST",
    body: {
      /* `isExtended` n'est pas posé : la colonne vaut `true` par défaut en base
         (migration `20250103141738`), et le back office ne l'envoie pas non plus. */
      model: {
        model: "UE",
        key,
        // `labelShort` sert les listes, `value` est le libellé du participant. Le back
        // office les distingue ; ici une seule saisie suffit, et la nuance se règle
        // ensuite champ par champ avec le libellé propre au formulaire.
        labelShort: name,
        value: name,
      },
      /* ⚠️ SEULEMENT S'IL Y EN A. Un tableau vide n'est pas « pas de choix » pour le
         serveur : il le passe à `insert([])`, qui lève « The query is empty », et une
         donnée sans choix (texte, nombre, pièce jointe) refusait de se créer. */
      ...(options.length
        ? {
            values: options.map((o) => ({
              key: freeKey(taken),
              value: o.label,
            })),
          }
        : {}),
      eventId,
    },
    signal,
  });

  const fresh = await fetchQuestions(domainId, eventId, signal);
  const created = fresh.find((q) => q.key === key);
  if (!created)
    throw new Error(
      "La donnée a été créée, mais le serveur ne la renvoie pas encore. Rechargez la page pour la retrouver.",
    );
  return created;
}

/**
 * Sept caractères tirés au sort, comme `generateRandomString` du back office — et
 * relus contre ce qui existe, parce qu'une collision serait silencieuse.
 */
export function freeKey(taken: Set<string>): string {
  const alphabet =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  for (let attempt = 0; attempt < 50; attempt += 1) {
    let key = "";
    for (let i = 0; i < 7; i += 1)
      key += alphabet[Math.floor(Math.random() * alphabet.length)];
    if (!taken.has(key)) {
      taken.add(key);
      return key;
    }
  }
  throw new Error("Impossible de tirer une clé libre.");
}

/**
 * LES QUESTIONS QU'UN FORMULAIRE A LE DROIT D'ACCUEILLIR.
 *
 * ⚠️ LA RÈGLE EST CELLE DU BACK OFFICE, À LA LETTRE : un formulaire de participant
 * prend les questions `U` **et** `UE` — les siennes, et celles qui ne valent que pour
 * cet événement — là où un formulaire de société ne prend que des `C`. Offrir une
 * question de société à un visiteur, c'est lui demander le SIRET de sa boîte au milieu
 * de son inscription, et l'écrire dans un enregistrement qu'il ne possède pas.
 */
export function questionsFor(
  type: Form["type"],
  questions: readonly Question[],
): Question[] {
  return questions.filter((q) =>
    type === "C" ? q.model === "C" : q.model === "U" || q.model === "UE",
  );
}

/** One record per model, each with its own `extended` bag. */
export type Answers = Partial<
  Record<
    Model,
    Record<string, unknown> & { extended?: Record<string, unknown> }
  >
>;

/**
 * À QUI CES RÉPONSES APPARTIENNENT — ce que la route lit dans sa requête.
 *
 * ⚠️ TROIS DESTINATAIRES, ET LA ROUTE NE TRAITE QUE CEUX QU'ON LUI NOMME. Le
 * formulaire du participant vise `eventId` + `userId` ; celui d'une société vise
 * `companyId`, et lui seul — passer un `userId` ferait relire et réécrire un
 * enregistrement que cet écran ne touche pas. Nommé plutôt que reconstruit à chaque
 * appel, parce que c'est exactement la ligne où une adresse mal formée écrit les bonnes
 * réponses au mauvais endroit.
 */
export type Target = {
  eventId?: number;
  userId?: number;
  /**
   * ⚠️ ZÉRO N'EST PAS « AUCUNE SOCIÉTÉ », C'EST « CRÉES-EN UNE ». La route cherche la
   * fiche par cet identifiant, n'en trouve aucune, et INSÈRE — puis rend l'identifiant
   * créé. C'est la façon dont la V1 crée une société (`companyId={company.id || 0}`),
   * et elle tient à un détail : la valeur voyage en chaîne, donc `"0"` est vrai côté
   * serveur là où `0` aurait été ignoré. À ne pas « corriger » en retirant le zéro.
   */
  companyId?: number;
};

const targetOf = (target: Target) =>
  new URLSearchParams(
    Object.entries(target).flatMap(([k, v]) =>
      v === undefined ? [] : [[k, String(v)] as [string, string]],
    ),
  ).toString();

export async function fetchAnswers(
  domainId: number,
  target: Target,
  signal?: AbortSignal,
): Promise<Answers> {
  const answer = await apiFetch<Answers>(
    `/domains/${domainId}/forms/data?${targetOf(target)}`,
    { api: "apix", signal },
  );
  return answer ?? {};
}

/**
 * ── UNE RÉPONSE À CHOIX EST UN OBJET `{ clé: true }` ──
 *
 * ⚠️ ET LA V2 ÉCRIVAIT AUTRE CHOSE : une chaîne pour les listes et les boutons, un
 * tableau pour les cases. L'API normalise à l'entrée — `helpers/normalizeExtended.js`
 * pose le format canonique noir sur blanc — donc ce qu'elle enregistrait était bien un
 * objet, mais elle ne savait plus le relire : au rechargement, les cases revenaient
 * DÉCOCHÉES au-dessus de réponses qui existaient. Ressaisir, et recommencer.
 *
 * La règle est celle de la V1 (`fo-meeting/src/components/FormBuilder.jsx`), et elle
 * vaut pour les quatre composants à options, étendus ou non.
 */

/**
 * LES CLÉS RETENUES, quelle que soit la forme sous laquelle la réponse arrive.
 *
 * ⚠️ TROIS FORMES SONT TOLÉRÉES, comme chez la V1, et ce n'est pas de la complaisance :
 * l'API ne migre pas ce qui est déjà en base (« Les valeurs déjà en base ne sont pas
 * migrées », dit son helper). Une chaîne écrite par un vieux client, un tableau écrit
 * par cette version de la V2, un objet canonique — les trois cohabitent chez les
 * participants d'aujourd'hui.
 */
export function chosen(value: unknown): string[] {
  if (value === null || value === undefined || value === "") return [];
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === "object")
    return Object.entries(value as Record<string, unknown>).flatMap(([k, v]) =>
      v ? [k] : [],
    );
  return [String(value)];
}

/**
 * LA FORME CANONIQUE — `{ clé: true }`, ou `null` quand rien n'est retenu.
 *
 * ⚠️ `null` ET NON `{}` NI `undefined`. Le serveur le dit pour l'objet vide : « un objet
 * vide passerait pour une réponse fournie et laisserait un champ obligatoire vide
 * franchir la validation ». Et `undefined` serait pire ici : `JSON.stringify` le retire
 * du corps, la clé disparaît, la fusion du serveur garde donc l'ANCIENNE réponse — tout
 * décocher deviendrait impossible.
 */
export function selection(
  keys: readonly string[],
): Record<string, true> | null {
  return keys.length
    ? Object.fromEntries(keys.map((c) => [c, true] as const))
    : null;
}

/** Reads a field's current value out of the answers. */
export function valueOf(answers: Answers, field: Field): unknown {
  const record = answers[field.model];
  if (!record) return undefined;
  return field.extended ? record.extended?.[field.key] : record[field.key];
}

/**
 * LES CHAMPS RÉELLEMENT VERROUILLÉS — et ça se décide sur ce que le SERVEUR détient
 * déjà, pas sur la définition du champ.
 *
 * ⚠️ « NON MODIFIABLE » NE VEUT PAS DIRE « GRISÉ D'EMBLÉE ». La règle est celle de la
 * V1, `valueOld && isDisabled` : le champ se ferme une fois qu'il PORTE une réponse.
 * Traduite en « toujours désactivé », un champ obligatoire marqué non modifiable
 * devenait impossible à remplir — donc une inscription impossible à terminer.
 *
 * ⚠️ ET UN OBJET N'EST PAS FORCÉMENT UNE RÉPONSE. Une réponse à choix est un
 * `{ clé: true }` ; `{}` — ou un objet dont toutes les options valent `false` — ne dit
 * RIEN. Testé comme une simple présence, il verrouillait un champ que personne n'avait
 * rempli, ce que la règle ci-dessus existe justement pour éviter.
 *
 * ⚠️ ÉCRITE DEUX FOIS AVANT D'ATTERRIR ICI (les informations, la société), avec la même
 * subtilité recopiée des deux côtés. Pure, donc testée une seule fois.
 */
export function lockedOf(
  fields: readonly Field[],
  answers: Answers,
): Set<string> {
  return new Set(
    fields
      .filter((f) => {
        if (!f.locked) return false;
        const stored = valueOf(answers, f);
        if (stored !== null && typeof stored === "object")
          return chosen(stored).length > 0;
        return stored !== undefined && stored !== null && stored !== "";
      })
      .map((f) => f.id),
  );
}

/**
 * LA RÉPONSE, RAMENÉE À CE QUI COMPTE pour dire si deux états sont le même : vide pour
 * « rien » (absent, `null`, `""`, liste vide, aucune option retenue), sinon le texte, ou
 * la liste triée des options retenues.
 */
function canonical(value: unknown): string {
  const picked =
    value !== null && typeof value === "object" ? chosen(value) : null;
  if (picked) return picked.length ? JSON.stringify([...picked].sort()) : "";
  return value === undefined || value === null ? "" : String(value);
}

/**
 * ═══ CE QU'ON S'APPRÊTE À ÉCRIRE DIT-IL AUTRE CHOSE QUE CE QUE LE SERVEUR DÉTIENT ? ═══
 *
 * ⚠️ « Continuer » RÉÉCRIVAIT LES MÊMES VALEURS À CHAQUE PASSAGE, et le journal en gardait
 * une trace (action 32, « mise à jour de l'utilisateur ») : quelqu'un qui repasse par ses
 * informations sans rien toucher laissait une ligne de journal par passage, que le back
 * office lit comme une modification (chasse du 2026-10-20). Rien n'a changé, rien n'est
 * écrit ni journalisé.
 *
 * Même règle que `answersToSend`, champ par champ : un champ verrouillé ou décoratif n'est
 * pas envoyé, un champ masqué par sa condition part vidé — donc compte comme un
 * changement s'il porte encore une réponse en base.
 */
export function changesAnything({
  fields,
  values,
  current,
  locked = new Set(),
}: {
  fields: ReadonlyArray<Field>;
  values: Record<string, unknown>;
  current: Answers;
  locked?: ReadonlySet<string>;
}): boolean {
  const shown = shownIds(fields, (f) => values[f.id]);
  return fields.some((field) => {
    if (DECORATIVE.has(field.component) || locked.has(field.id)) return false;
    const sent = shown.has(field.id) ? values[field.id] : null;
    return canonical(sent) !== canonical(valueOf(current, field));
  });
}

/**
 * WRITES BACK, one record per model, `extended` MERGED.
 *
 * ⚠️ The bag holds far more than this form's fields — another form's answers, the
 * waiting list, whatever else the product put there. Sending only what this screen
 * collected would delete the rest.
 *
 * ⚠️ DES ARGUMENTS NOMMÉS, et ce n'est pas un goût : ils étaient sept, dont trois
 * identifiants numériques d'affilée. Deux appels existent maintenant — le participant
 * et la société — et intervertir `eventId` et `userId` en écrivait un dans l'autre
 * sans qu'aucun type ne s'y oppose.
 *
 * Rend L'IDENTIFIANT ÉCRIT quand la route en crée un : c'est le seul moyen de
 * connaître celui d'une société qu'on vient de créer, la route ne rendant rien d'autre.
 */
/**
 * LE CORPS ENVOYÉ, à partir de ce que l'écran tient. Séparé de l'envoi pour être testé.
 *
 * ⚠️ UN CHAMP CACHÉ PAR SA CONDITION PART VIDÉ, PAS IGNORÉ. Ignoré, sa vieille réponse
 * resterait en base et réapparaîtrait au prochain « oui » — ou, pire, serait exportée
 * comme une réponse donnée. C'est le nettoyage de la V1
 * (`cleanHiddenConditionalValues`), au même endroit qu'elle : juste avant d'envoyer.
 */
export function answersToSend({
  fields,
  values,
  current,
  locked = new Set(),
  base,
}: {
  fields: ReadonlyArray<Field>;
  values: Record<string, unknown>;
  current: Answers;
  locked?: ReadonlySet<string>;
  base?: Partial<Record<Model, Record<string, unknown>>>;
}): Partial<Record<Model, Record<string, unknown> & { extended?: Record<string, unknown> }>> {
  type Record_ = Record<string, unknown> & {
    extended?: Record<string, unknown>;
  };
  const body: Partial<Record<Model, Record_>> = {};
  for (const [model, values] of Object.entries(base ?? {})) {
    body[model as Model] = { ...values };
  }
  const shown = shownIds(fields, (f) => values[f.id]);

  for (const field of fields) {
    // ⚠️ `verrouilles` PLUTÔT QUE `field.locked` : un champ non modifiable qui n'a
    // pas encore de valeur doit pouvoir être écrit une première fois, sinon la
    // réponse qu'on vient de saisir n'arrive jamais en base.
    if (DECORATIVE.has(field.component) || locked.has(field.id)) continue;
    const sent = shown.has(field.id) ? values[field.id] : null;
    const record: Record_ = (body[field.model] ??= {});
    if (field.extended) {
      const bag = (record.extended ??= {
        ...(current[field.model]?.extended ?? {}),
      });
      bag[field.key] = sent;
    } else {
      record[field.key] = sent;
    }
  }
  return body;
}

export async function saveAnswers({
  domainId,
  target,
  fields,
  values,
  current,
  locked = new Set(),
  base,
}: {
  domainId: number;
  target: Target;
  fields: Field[];
  values: Record<string, unknown>;
  current: Answers;
  /** Les champs que l'écran a rendus non modifiables — ils ne sont pas renvoyés. */
  locked?: ReadonlySet<string>;
  /**
   * CE QUI S'ÉCRIT SOUS LES RÉPONSES — des colonnes que le formulaire ne porte pas.
   *
   * ⚠️ L'IDENTIFIANT D'UNE SOCIÉTÉ EST DANS CE CAS, et il le faut : il est saisi à
   * l'écran d'avant, pas dans le formulaire, et sans lui la fiche créée n'aurait rien
   * qui la distingue — donc rien à quoi le participant suivant puisse se rattacher.
   * Les réponses PASSENT DEVANT, comme la V1 : un formulaire qui pose la question
   * lui-même a le dernier mot.
   */
  base?: Partial<Record<Model, Record<string, unknown>>>;
}): Promise<number | undefined> {
  type Record_ = Record<string, unknown> & {
    extended?: Record<string, unknown>;
  };
  const body: Partial<Record<Model, Record_>> = {};
  for (const [model, values] of Object.entries(base ?? {})) {
    body[model as Model] = { ...values };
  }

  Object.assign(body, answersToSend({ fields, values, current, locked, base }));

  const answer = await apiFetch<{ id?: number } | undefined>(
    `/domains/${domainId}/forms/data?${targetOf(target)}`,
    { api: "apix", method: "PUT", body },
  );
  return typeof answer?.id === "number" ? answer.id : undefined;
}
